import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { motion, useScroll, useTransform } from "framer-motion";
import { X } from "lucide-react";

// Separate Component: Event Card
const EventCard = ({ event, onImageClick }) => {
  const isExternal = event.link.startsWith("http");

  return (
    <motion.div
      className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 cursor-pointer group"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5, boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)" }}
      onClick={() => onImageClick(event.image)}
    >
      <div className="relative aspect-video">
        <img
          src={event.image}
          alt={event.title}
          className="w-full h-full object-cover object-center transition-transform group-hover:scale-105 duration-300"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = "https://via.placeholder.com/400x225?text=Event";
          }}
        />
        <span className="absolute top-3 left-3 bg-gradient-to-r from-primary to-blue-600 text-white text-sm px-3 py-1 rounded-full shadow-md">
          {event.date}
        </span>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-1">
          {event.title}
        </h3>
        <p className="text-primary mb-3">{event.location}</p>
        <p className="text-gray-600 line-clamp-3 mb-4">{event.description}</p>

        {isExternal ? (
          <a
            href={event.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-primary text-white px-5 py-2 rounded-full font-medium hover:bg-blue-700 transition"
          >
            {event.linkText}
          </a>
        ) : (
          <Link
            to={event.link}
            className="inline-block bg-primary text-white px-5 py-2 rounded-full font-medium hover:bg-blue-700 transition"
          >
            {event.linkText}
          </Link>
        )}
      </div>
    </motion.div>
  );
};

function Events() {
  const { t } = useTranslation();
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, 50]);

  // Featured Event
  const featuredEvent = {
    title: t("events.featuredTitle"),
    date: t("events.featuredDate"),
    location: t("events.featuredLocation"),
    image: "/assets/images/outreach.jpg",
    link: t("events.featuredLinkUrl"),
    linkText: t("events.featuredLink"),
  };

  // Upcoming Events - Clean data
  const upcomingEvents = [
    {
      title: t("events.event1Title"),
      date: t("events.event1Date"),
      location: t("events.event1Location"),
      description: t("events.event1Description"),
      image: "/assets/images/event10.jpeg",
      link: "/contact",
      linkText: t("events.event1Link"),
    },
    {
      title: t("events.event3Title"),
      date: t("events.event3Date"),
      location: t("events.event3Location"),
      description: t("events.event3Description"),
      image: "/assets/images/upcoming.jpeg",
      link: "https://www.youtube.com/@NAOGM21",
      linkText: t("events.event3Link"),
    },
    {
      title: t("events.event2Title"),
      date: t("events.event2Date"),
      location: t("events.event2Location"),
      description: t("events.event2Description"),
      image: "/assets/images/updates-noagm.jpeg",
      link: "/contact",
      linkText: t("events.event2Link"),
    },
  ];

  const openFullscreen = (image) => setFullscreenImage(image);
  const closeFullscreen = () => setFullscreenImage(null);

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>{t("events.seoTitle")}</title>
        <meta name="description" content={t("events.seoDescription")} />
      </Helmet>

      {/* HERO SECTION */}
      <motion.section
        className="relative h-[50vh] xs:h-[60vh] sm:h-[70vh] md:h-[80vh] overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${featuredEvent.image})`, y: heroY }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70" />

        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-extrabold text-white drop-shadow-xl max-w-4xl">
            {featuredEvent.title}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-100 mt-3 sm:mt-4">
            {featuredEvent.date} • {featuredEvent.location}
          </p>
          <motion.a
            href={featuredEvent.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 bg-primary text-white px-6 sm:px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {featuredEvent.linkText}
          </motion.a>
        </motion.div>
      </motion.section>

      {/* FEATURED INFO CARD */}
      <section className="relative z-10 px-4 -mt-12 xs:-mt-16 sm:-mt-20">
        <div className="container mx-auto max-w-[95%] sm:max-w-4xl">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -5, boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)" }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              About This Event
            </h2>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-primary font-medium">
              <span>{featuredEvent.date}</span>
              <span>{featuredEvent.location}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="bg-gray-100 py-12 sm:py-16 px-4">
        <div className="container mx-auto max-w-[95%] xl:max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10">
            {t("events.upcomingSectionTitle")}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {upcomingEvents.map((event, index) => (
              <EventCard 
                key={index} 
                event={event} 
                onImageClick={openFullscreen} 
              />
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="bg-gradient-to-r from-primary to-blue-700 text-white py-12 sm:py-16 text-center px-4">
        <div className="container mx-auto max-w-[95%] xl:max-w-7xl">
          <motion.h2
            className="text-3xl md:text-4xl font-bold mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {t("events.ctaTitle")}
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/contact"
              className="inline-block bg-white text-primary px-8 py-3 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t("events.ctaButton")}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FULLSCREEN MODAL */}
      <FullscreenModal
        image={fullscreenImage}
        onClose={closeFullscreen}
      />
    </div>
  );
}

// Separate Modal Component
const FullscreenModal = ({ image, onClose }) => {
  if (!image) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        className="absolute top-6 right-6 text-white p-3 rounded-full hover:bg-white/20 transition z-10"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
      >
        <X size={32} />
      </button>

      <motion.img
        src={image}
        alt="Event Preview"
        className="max-w-[95%] max-h-[90vh] rounded-lg shadow-2xl object-contain"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
};

export default Events;